/* ==========================================================================
   lockin — movimiento ligado al scroll
   1. Utilidades
   2. Aparición progresiva (respaldo con IntersectionObserver)
   3. Hero: zoom del producto al bajar (respaldo sin scroll-driven animations)
   4. Escena fijada: ángulos, secuencia de fotogramas, modelo 3D y contador
   Nada de esto se ejecuta con «reducir movimiento»: el contenido queda estático.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var enabled = root.classList.contains('has-motion');
  var supportsTimeline = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));

  if (!enabled || reduced) {
    root.classList.add('motion-static');
    return;
  }
  root.classList.add('motion-on');

  /* 1. Utilidades
     ------------------------------------------------------------------------ */
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /* Un único listener de scroll para todo, agrupado por fotograma. */
  var tasks = [];
  var ticking = false;
  function run() {
    ticking = false;
    for (var i = 0; i < tasks.length; i += 1) tasks[i]();
  }
  function schedule() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(run);
  }
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  /* 2. Aparición progresiva
     ------------------------------------------------------------------------ */
  var REVEAL =
    '.section__head, .steps__item, .features__list, .guarantee__list, .timer-demo__panel, ' +
    '.final-cta__panel, .benefits__item, .faq__list, .compare__col, .testimonial, .rich-text .rte, .reviews__aside';

  function initReveal(scope) {
    if (supportsTimeline) return; /* lo hace el CSS con animation-timeline: view() */
    var items = $$(REVEAL, scope).filter(function (el) {
      return !el.closest('main > .shopify-section:first-child') && !el.classList.contains('reveal');
    });
    if (!items.length || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach(function (el) {
      el.classList.add('reveal');
      observer.observe(el);
    });
  }

  /* 3. Hero
     ------------------------------------------------------------------------ */
  function initHero() {
    if (supportsTimeline) return;
    var hero = document.querySelector('.template-index main > .shopify-section:first-child .product--lead');
    if (!hero) return;
    tasks.push(function () {
      var rect = hero.getBoundingClientRect();
      var progress = clamp(-rect.top / Math.max(rect.height, 1), 0, 1);
      hero.style.setProperty('--hero-p', progress.toFixed(3));
    });
  }

  /* 4. Escena fijada
     ------------------------------------------------------------------------ */
  function pad(number, digits) {
    var text = String(number);
    while (text.length < digits) text = '0' + text;
    return text;
  }

  function initSpin(section) {
    if (section.dataset.spinReady) return;
    section.dataset.spinReady = 'true';

    var track = section.querySelector('[data-spin-track]');
    var stage = section.querySelector('.spin__stage');
    var bar = section.querySelector('[data-spin-bar]');
    var counter = section.querySelector('[data-spin-count]');
    var mode = section.dataset.mode;
    var near = false;
    var last = -1;
    if (!track || !stage) return;

    section.classList.add('is-live');

    /* --- contador --- */
    var countStart = counter ? parseFloat(counter.dataset.start) || 0 : 0;
    var countEnd = counter ? parseFloat(counter.dataset.end) || 0 : 0;
    var countStep = Math.abs(countEnd - countStart) >= 100 ? 5 : 1;

    /* --- ángulos --- */
    var angles = mode === 'illustration' ? [] : $$('[data-spin-angle]', section);

    var variantInputs = $$('[data-spin-variant]', section);

    function currentVariant() {
      for (var i = 0; i < variantInputs.length; i += 1) {
        if (variantInputs[i].checked) return variantInputs[i];
      }
      return null;
    }

    /* Solo cuentan las fotos del color elegido (las demás las oculta el CSS). */
    function activeAngles() {
      var input = currentVariant();
      if (!input) return angles;
      return angles.filter(function (angle) {
        return !angle.dataset.variant || angle.dataset.variant === input.value;
      });
    }

    function drawAngles(progress) {
      var shown = activeAngles();
      var position = progress * (shown.length - 1);
      shown.forEach(function (angle, index) {
        var distance = index - position;
        var opacity = clamp(1 - Math.abs(distance), 0, 1);
        angle.style.opacity = opacity.toFixed(3);
        angle.style.transform =
          'perspective(1200px) rotateY(' + (distance * -38).toFixed(2) + 'deg) scale(' + (1 - Math.min(Math.abs(distance), 1) * 0.08).toFixed(3) + ')';
        angle.classList.toggle('is-current', Math.abs(distance) < 0.5);
      });
    }

    /* --- secuencia --- */
    var canvas = section.querySelector('[data-spin-canvas]');
    var poster = section.querySelector('[data-spin-poster]');
    var context = canvas ? canvas.getContext('2d') : null;
    var sets = $$('[data-spin-sequence]', section).map(function (el) {
      return {
        firstUrl: el.dataset.firstUrl,
        firstNumber: el.dataset.firstNumber,
        count: parseInt(el.dataset.count, 10) || 0,
        digits: parseInt(el.dataset.digits, 10) || 3,
        color: el.dataset.color,
        frames: [],
        started: false,
      };
    });
    var activeSet = 0;
    var targetFrame = 0;

    function frameUrl(set, index) {
      /* El primer archivo da la URL real del asset; el resto solo cambia el número. */
      var marker = set.firstNumber + '.';
      var at = set.firstUrl.lastIndexOf(marker);
      if (at < 0) return set.firstUrl;
      return set.firstUrl.slice(0, at) + pad(index + 1, set.digits) + set.firstUrl.slice(at + set.firstNumber.length);
    }

    function paint() {
      var set = sets[activeSet];
      if (!set || !context) return;
      /* Fotograma cargado más cercano al que toca: mientras llegan todos, el giro va a saltos. */
      var best = null;
      for (var offset = 0; offset < set.count; offset += 1) {
        var before = set.frames[targetFrame - offset];
        var after = set.frames[targetFrame + offset];
        if (before && before.complete && before.naturalWidth) best = before;
        else if (after && after.complete && after.naturalWidth) best = after;
        if (best) break;
      }
      if (!best) return;
      if (canvas.hidden) {
        canvas.hidden = false;
        if (poster) poster.hidden = true;
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
      var scale = Math.min(canvas.width / best.naturalWidth, canvas.height / best.naturalHeight);
      var width = best.naturalWidth * scale;
      var height = best.naturalHeight * scale;
      context.drawImage(best, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
    }

    function loadSet(set) {
      if (set.started || set.count < 2) return;
      set.started = true;
      /* Carga progresiva: primero 1 de cada 8, luego 1 de cada 4, de cada 2 y el resto. */
      var order = [];
      [8, 4, 2, 1].forEach(function (stride) {
        for (var index = 0; index < set.count; index += stride) {
          if (order.indexOf(index) < 0) order.push(index);
        }
      });
      var cursor = 0;
      function next() {
        if (cursor >= order.length) return;
        var index = order[cursor];
        cursor += 1;
        var image = new Image();
        image.decoding = 'async';
        image.onload = function () {
          if (sets[activeSet] === set) paint();
          next();
        };
        image.onerror = next;
        image.src = frameUrl(set, index);
        set.frames[index] = image;
      }
      /* Cuatro descargas en paralelo como mucho. */
      for (var lane = 0; lane < 4; lane += 1) next();
    }

    function tint() {
      var set = sets[activeSet];
      var input = currentVariant();
      if (mode === 'sequence' && set && set.color) stage.style.setProperty('--spin-tint', set.color);
      else if (input && input.dataset.color) stage.style.setProperty('--spin-tint', input.dataset.color);
    }

    variantInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        tint();
        last = -1;
        update();
      });
    });

    $$('[data-spin-set]', section).forEach(function (button) {
      button.addEventListener('click', function () {
        activeSet = parseInt(button.dataset.spinSet, 10) || 0;
        $$('[data-spin-set]', section).forEach(function (other) {
          other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
        });
        tint();
        loadSet(sets[activeSet]);
        paint();
      });
    });

    /* --- ilustración 3D --- */
    var box = section.querySelector('[data-spin-3d]');

    /* --- modelo 3D --- */
    var model = section.querySelector('[data-spin-model] model-viewer');

    function update() {
      if (!near) return;
      var rect = track.getBoundingClientRect();
      var travel = rect.height - stage.offsetHeight;
      var top = parseFloat(window.getComputedStyle(stage).top) || 0;
      var progress = travel > 0 ? clamp((top - rect.top) / travel, 0, 1) : 0;
      if (Math.abs(progress - last) < 0.0005) return;
      last = progress;

      if (bar) bar.style.transform = 'scaleX(' + progress.toFixed(4) + ')';
      if (counter) {
        var eased = 1 - Math.pow(1 - progress, 2);
        var value = countStart + (countEnd - countStart) * eased;
        counter.textContent = String(progress >= 0.995 ? countEnd : Math.round(value / countStep) * countStep);
      }
      if (mode === 'sequence' && sets[activeSet]) {
        targetFrame = Math.round(progress * (sets[activeSet].count - 1));
        paint();
      } else if (mode === 'illustration' && box) {
        /* Una vuelta completa: empieza y acaba en tres cuartos, enseñando el frente. */
        var turn = progress * 360 - 32;
        box.style.setProperty('--box-ry', turn.toFixed(1) + 'deg');
        if (box.__gl) box.__gl.render(turn);
        /* La franja de luz cruza cada cara mientras esa cara mira hacia delante. */
        var front = ((turn + 180) % 360 + 360) % 360 - 180;
        var back = ((turn % 360) + 360) % 360 - 180;
        box.style.setProperty('--glint-f', (50 + front * 0.75).toFixed(1) + '%');
        box.style.setProperty('--glint-b', (50 + back * 0.75).toFixed(1) + '%');
      } else if (mode === 'model' && model) {
        model.setAttribute('camera-orbit', (progress * 360).toFixed(1) + 'deg 75deg auto');
      } else if (angles.length) {
        drawAngles(progress);
      }
    }

    tasks.push(update);
    tint();

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          near = entries[0].isIntersecting;
          if (near) {
            if (mode === 'sequence' && sets[activeSet]) loadSet(sets[activeSet]);
            last = -1;
            update();
          }
        },
        { rootMargin: '100% 0px 100% 0px' }
      ).observe(section);
    } else {
      near = true;
      if (mode === 'sequence' && sets[activeSet]) loadSet(sets[activeSet]);
    }
    update();
  }

  function init(scope) {
    initReveal(scope);
    $$('[data-spin]', scope).forEach(initSpin);
  }

  initHero();
  init(document);
  schedule();

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    schedule();
  });
})();
